import { useCallback, useEffect, useMemo, useState } from "react";
import { Paper, } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsSidebar from "../components/AnalyticsSidebar";
import AnalyticsGraph from "../components/AnalyticsGraph";
import "../styles/analytics.scss";
import { globalGetService } from "../../../utils/globalApiServices";
import { getGraphListApi, getMaintenanceChecksApi, extractMaintenanceEvents } from "../apiService";
import { checkApiStatus, } from "../../../utils";
import AnalyticsSkeleton from "../components/AnalyticsSkeleton";
import { getAssetListApi } from "../../leases/apiServices";
import { getCurrencyAc, getLesseeApi, getOwnerListApi } from "../../../shared_elements/actionCreators";
import { buildAnalyticsFilterMenu } from "../analyticsFilterMenu";
import { trackActivity } from "../../../utils/mixpanel";

const CURRENCY_OPTIONS_FALLBACK = [
  { id: 27, code: "GBP" },
  { id: 3, code: "EURO" },
  { id: 1, code: "USD" },
];

const Analytics = () => {
  const dispatch = useDispatch();
  const { lessee, owner, currencies } = useSelector((state) => state.leases);

  const currencyOptions = currencies?.length > 0 ? currencies : CURRENCY_OPTIONS_FALLBACK;
  const [selectedGraph, setSelectedGraph] = useState({});
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [graphList, setGraphList] = useState({});
  const [assetList, setAssetList] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const analyticsFilterMenu = useMemo(
    () =>
      buildAnalyticsFilterMenu(
        assetList,
        lessee || [],
        owner || [],
        currencyOptions,
        eventTypeOptions,
      ),
    [assetList, lessee, owner, currencyOptions, eventTypeOptions],
  );

  /** Keep graph list + current category in sync after pin/unpin API success */
  const updateGraphPinnedState = useCallback((graphId, isPinned) => {
    const patchGraph = (g) => {
      if (g.id !== graphId) return g;
      return {
        ...g,
        is_pinned: isPinned,
        user_pinned: {
          ...(g.user_pinned || {}),
          is_pinned: isPinned,
          graph: graphId,
        },
      };
    };

    setGraphList((prev) => {
      const next = { ...prev };
      for (const category of Object.keys(next)) {
        const graphs = next[category];
        if (!Array.isArray(graphs)) continue;
        next[category] = graphs.map(patchGraph);
      }
      return next;
    });

    setSelectedGraph((sel) => {
      if (!sel?.graphsData) return sel;
      return {
        ...sel,
        graphsData: sel.graphsData.map(patchGraph),
      };
    });
  }, []);

  const getGraphList = () => {
    setSkeletonLoader(true);
    globalGetService(getGraphListApi()).then((response) => {
      if (checkApiStatus(response)) {
        setSkeletonLoader(false);
        setGraphList(response.data.data);
      } else {
        setSkeletonLoader(false);
        setGraphList({});
      }
    });
  };

  useEffect(() => {
    getGraphList();
    trackActivity('Page Viewed', { page_title: 'Analytics' })
  }, []);

  useEffect(() => {
    globalGetService(getAssetListApi()).then((response) => {
      if (checkApiStatus(response)) {
        setAssetList(response?.data?.data || []);
      }
    });
  }, []);

  useEffect(() => {
    globalGetService(getMaintenanceChecksApi()).then((response) => {
      if (checkApiStatus(response)) {
        setEventTypeOptions(extractMaintenanceEvents(response?.data?.data));
      }
    });
  }, []);

  useEffect(() => {
    if (!lessee?.length) dispatch(getLesseeApi());
    if (!owner?.length) dispatch(getOwnerListApi());
    if (!currencies?.length) dispatch(getCurrencyAc({}));
  }, [dispatch, lessee?.length, owner?.length, currencies?.length]);

  return (
    <Paper className="lease-details-section section-overflow analytics-page lease-module-paper" elevation={0}>
      {skeletonLoader ? (
        <AnalyticsSkeleton graphList={graphList} />
      ) : (
        <>
          <AnalyticsSidebar
            selectedGraph={selectedGraph}
            setSelectedGraph={setSelectedGraph}
            graphList={graphList}
          />
          <div className="lease-details-content analytics-page analytics-content-pane">
            <AnalyticsGraph
              selectedGraph={selectedGraph}
              onPinnedChange={updateGraphPinnedState}
              analyticsFilterMenu={analyticsFilterMenu}
            />
          </div>
        </>
      )}
    </Paper>
  );
};

export default Analytics;